import { useEffect, useState } from "react";
import { api, asset, PHOTO_CATEGORIES } from "../lib/api";
import { PrintOrderModal } from "../components/PrintOrderModal";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Printer, Lock } from "lucide-react";

export default function Portfolio() {
  const [photos, setPhotos] = useState([]);
  const [cat, setCat] = useState("All");
  const [catalog, setCatalog] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [light, setLight] = useState(null);

  useEffect(() => {
    document.title = "Portfolio — Scott Burton Photography";
    api.get("/pricing").then(({ data }) => setCatalog(data));
  }, []);
  useEffect(() => {
    const params = { portfolio: true };
    if (cat !== "All") params.category = cat;
    api.get("/photos", { params }).then(({ data }) => setPhotos(data));
  }, [cat]);

  const buy = (p) => { setLight(null); setCtx({ kind: "photo", title: p.title || p.category, photoId: p.id, imageUrl: p.image_url }); };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="overline-label mb-3">Selected work · Oxford, Mississippi</div>
      <h1 className="font-serif text-5xl lg:text-7xl font-light">Portfolio</h1>
      <p className="mt-4 text-neutral-400 max-w-2xl">
        Weddings, sports and photojournalism. Wedding galleries are here to view — everything else is
        available as a fine-art print.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {PHOTO_CATEGORIES.map((c) => (
          <button key={c} data-testid={`portfolio-filter-${c}`} onClick={() => setCat(c)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
              cat === c ? "border-amber-glow text-amber-glow bg-amber-glow/10" : "border-white/12 text-neutral-300 hover:border-white/30"
            }`}>
            {c}
          </button>
        ))}
      </div>

      {photos.length === 0 ? (
        <div className="py-32 text-center text-neutral-500">No images in this category yet.</div>
      ) : (
        <div className="mt-10 columns-2 md:columns-3 xl:columns-4 gap-4">
          {photos.map((p, i) => (
            <div key={p.id} data-testid={`portfolio-item-${p.id}`}
              className="mb-4 break-inside-avoid relative group rounded-lg overflow-hidden border border-white/10 cursor-pointer animate-fade-up"
              style={{ animationDelay: `${(i % 12) * 40}ms` }}
              onClick={() => setLight(p)}>
              <img src={asset(p.image_url)} alt={p.title || p.category} loading="lazy" className="w-full block transition-transform duration-[1200ms] group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 inset-x-0 p-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="overline-label">{p.category}</span>
                {p.for_sale
                  ? <span className="text-xs flex items-center gap-1 text-amber-glow"><Printer size={12} /> Print</span>
                  : <span className="text-xs flex items-center gap-1 text-neutral-400"><Lock size={11} /> View</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* lightbox */}
      <Dialog open={!!light} onOpenChange={(o) => !o && setLight(null)}>
        <DialogContent data-testid="portfolio-lightbox" className="max-w-5xl w-[95vw] bg-obsidian-800 border-white/10 p-3">
          <DialogTitle className="sr-only">{light?.category} photograph</DialogTitle>
          {light && (
            <div>
              <img src={asset(light.image_url)} alt={light.category} className="w-full max-h-[75vh] object-contain bg-black" />
              <div className="flex items-center justify-between mt-3 px-1">
                <div>
                  <div className="overline-label">{light.category}</div>
                  {light.title && <div className="font-serif text-xl">{light.title}</div>}
                </div>
                {light.for_sale
                  ? <button data-testid="lightbox-buy" onClick={() => buy(light)} className="btn-amber rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Printer size={14} /> Order as print</button>
                  : <span className="text-xs text-neutral-500 flex items-center gap-1"><Lock size={12} /> Gallery only</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PrintOrderModal open={!!ctx} onClose={() => setCtx(null)} catalog={catalog} context={ctx} />
    </div>
  );
}
